import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { IdCard, Percent, Gift, Star, Calendar, Sparkles, ShieldCheck, ArrowLeft, Check, Loader2, CheckCircle2, Download } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const tiers = [
  {
    slug: 'decouverte',
    name: 'Découverte',
    price: 'Gratuite',
    tag: 'à l\'inscription',
    color: 'from-slate-500 to-slate-700',
    ring: 'ring-slate-200',
    perks: [
      'Newsletter mensuelle Pierre & Edois',
      'Accès prioritaire aux évènements ouverts',
      '5 % de fidélité cumulés sur chaque réservation',
      'Numéro de membre personnel',
    ],
  },
  {
    slug: 'essentielle',
    name: 'Essentielle',
    price: '25 000 FCFA',
    tag: 'par an',
    color: 'from-orange-400 to-orange-600',
    ring: 'ring-orange-200',
    popular: true,
    perks: [
      'Tout le niveau Découverte',
      '−10 % sur toutes les balades',
      '−15 % sur la boutique en ligne',
      '1 balade Essentielle offerte à l\'anniversaire',
      'Réservation prioritaire les week-ends',
    ],
  },
  {
    slug: 'prestige',
    name: 'Prestige',
    price: '75 000 FCFA',
    tag: 'par an',
    color: 'from-amber-500 via-yellow-500 to-orange-500',
    ring: 'ring-amber-200',
    perks: [
      'Tout le niveau Essentielle',
      '−20 % sur toutes les balades',
      '−25 % sur la boutique',
      '2 balades Premium offertes / an',
      'Accès aux évènements privés OLS',
      'Ligne WhatsApp dédiée · réservation express',
    ],
  },
];

const benefits = [
  { icon: Percent, title: 'Réductions permanentes', desc: 'Économies sur chaque balade et achat boutique.' },
  { icon: Gift, title: 'Cadeaux fidélité', desc: 'Balade offerte à l\'anniversaire et surprises annuelles.' },
  { icon: Calendar, title: 'Priorité réservation', desc: 'Places garanties même en pleine saison.' },
  { icon: Sparkles, title: 'Évènements exclusifs', desc: 'Sorties privatisées, ateliers, before-parties.' },
  { icon: Star, title: 'Points de fidélité', desc: 'Cumulez, échangez contre des activités gratuites.' },
  { icon: ShieldCheck, title: 'Support prioritaire', desc: 'Ligne dédiée, réponse sous 2 h ouvrées.' },
];

const CartMembrePage = () => {
  const [selectedTier, setSelectedTier] = useState('essentielle');
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null); // pour le tier gratuit

  const handleChooseTier = (tierId) => {
    setSelectedTier(tierId);
    setError('');
    setSuccess(null);
    setTimeout(() => {
      document.getElementById('adhesion-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(null);
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      setError('Merci de remplir tous les champs.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/members/adhere`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        tier: selectedTier,
      });
      if (data.checkout_url) {
        // Payant → redirect FedaPay
        window.location.href = data.checkout_url;
      } else {
        // Gratuit → confirmation immédiate
        setSuccess(data);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg;
      if (Array.isArray(detail)) msg = detail[0]?.msg || 'Champs invalides';
      else if (typeof detail === 'string') msg = detail;
      else msg = err.message || 'Erreur lors de l\'adhésion. Réessayez.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main className="pt-28 pb-20">
        {/* Hero */}
        <section className="relative bg-gradient-to-br from-orange-50 via-white to-amber-50 py-16 px-6 overflow-hidden">
          <div className="max-w-5xl mx-auto text-center relative z-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold uppercase tracking-widest mb-6">
              <IdCard className="w-3.5 h-3.5" />
              Programme fidélité
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6" data-testid="carte-title">
              Votre carte de membre <span className="text-orange-500">Pierre &amp; Edois</span>
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Rejoignez notre communauté, profitez d'avantages exclusifs et participez à
              l'aventure d'un tourisme béninois qui privilégie l'authentique et le local.
            </p>
          </div>
          {/* Decorative blur */}
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-orange-200/40 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-amber-200/40 rounded-full blur-3xl"></div>
        </section>

        {/* 6 avantages */}
        <section className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">Vos avantages</h2>
              <div className="w-16 h-1 bg-orange-500 rounded-full mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((b, i) => {
                const Icon = b.icon;
                return (
                  <div
                    key={i}
                    className="bg-white border border-slate-100 rounded-2xl p-6 hover:border-orange-200 hover:shadow-lg transition-all duration-300"
                    data-testid={`carte-benefit-${i}`}
                  >
                    <div className="w-11 h-11 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{b.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Formules */}
        <section className="py-16 px-6 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">Nos formules</h2>
              <div className="w-16 h-1 bg-orange-500 rounded-full mx-auto"></div>
              <p className="text-slate-600 mt-4">3 niveaux · Rejoignez celui qui vous ressemble</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {tiers.map((tier) => (
                <div
                  key={tier.slug}
                  className={`relative bg-white rounded-3xl p-8 shadow-sm ring-1 ${tier.ring} ${
                    tier.popular ? 'md:-translate-y-4 ring-2 ring-orange-400 shadow-xl' : ''
                  } transition-all`}
                  data-testid={`carte-tier-${tier.slug}`}
                >
                  {tier.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wider shadow-md">
                      Le plus choisi
                    </div>
                  )}
                  <div className={`inline-block px-3 py-1 rounded-full bg-gradient-to-r ${tier.color} text-white text-xs font-bold uppercase tracking-widest mb-4`}>
                    {tier.name}
                  </div>
                  <div className="mb-6">
                    <div className="text-3xl font-bold text-slate-900">{tier.price}</div>
                    <div className="text-sm text-slate-500 mt-1">{tier.tag}</div>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {tier.perks.map((perk, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                        <Check className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                        <span>{perk}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => handleChooseTier(tier.slug)}
                    className={`block w-full text-center py-3 rounded-xl font-semibold transition-all ${
                      tier.popular
                        ? 'bg-orange-500 text-white hover:bg-orange-600 shadow-md hover:shadow-lg'
                        : 'bg-slate-900 text-white hover:bg-slate-800'
                    }`}
                    data-testid={`carte-tier-cta-${tier.slug}`}
                  >
                    Je choisis
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comment ça marche */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
                Comment devenir membre&nbsp;?
              </h2>
              <div className="w-16 h-1 bg-orange-500 rounded-full mx-auto"></div>
            </div>

            <div className="space-y-6">
              {[
                {
                  n: '1',
                  title: 'Choisissez votre formule',
                  desc: 'Découverte, Essentielle ou Prestige selon vos envies et votre budget.',
                },
                {
                  n: '2',
                  title: 'Réservez et payez en ligne',
                  desc: 'Paiement sécurisé via FedaPay (Mobile Money, carte bancaire, virement).',
                },
                {
                  n: '3',
                  title: 'Recevez votre carte',
                  desc: 'Carte numérique instantanée + carte physique livrée sous 5 jours à Cotonou.',
                },
                {
                  n: '4',
                  title: 'Profitez immédiatement',
                  desc: 'Vos réductions et avantages s\'appliquent dès votre prochaine réservation.',
                },
              ].map((step) => (
                <div
                  key={step.n}
                  className="flex gap-5 p-5 bg-white rounded-2xl border border-slate-100 hover:border-orange-200 transition-all"
                  data-testid={`carte-step-${step.n}`}
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 text-white flex items-center justify-center font-bold text-lg shadow-md">
                    {step.n}
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-1">{step.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Formulaire d'adhésion */}
        <section id="adhesion-form" className="py-16 px-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
                Prêt à viser plus loin&nbsp;?
              </h2>
              <p className="text-slate-300 leading-relaxed">
                Formule sélectionnée&nbsp;: <span className="font-bold text-orange-400">
                  {tiers.find(t => t.slug === selectedTier)?.name}
                </span>
                {' · '}
                <span className="text-white font-semibold">
                  {tiers.find(t => t.slug === selectedTier)?.price}
                </span>
              </p>
            </div>

            {success ? (
              /* Confirmation adhésion gratuite */
              <div className="bg-white rounded-2xl p-8 shadow-xl" data-testid="carte-success">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Bienvenue dans la famille&nbsp;!</h3>
                  <p className="text-slate-600 mb-4">Votre carte {success.tier_label || 'Découverte'} est activée.</p>
                  <div className="inline-block bg-orange-50 border-2 border-orange-200 rounded-xl px-6 py-3 mb-6">
                    <div className="text-xs uppercase tracking-widest text-orange-600 font-semibold mb-1">Votre numéro de membre</div>
                    <div className="text-2xl font-bold text-slate-900 font-mono">{success.member_number}</div>
                  </div>
                  <p className="text-sm text-slate-500 mb-6">
                    Nous vous avons envoyé un récapitulatif par email. Conservez précieusement votre numéro
                    de membre — il vous servira à chaque réservation.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    {success.reference && (
                      <a
                        href={`${API_URL}/api/members/${success.reference}/card.pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold transition-colors"
                        data-testid="carte-success-download"
                      >
                        <Download className="w-4 h-4" />
                        Télécharger ma carte PDF
                      </a>
                    )}
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold transition-colors"
                      data-testid="carte-success-home"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Retour à l'accueil
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl space-y-5" data-testid="carte-adhesion-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                      Prénom
                    </label>
                    <input
                      type="text"
                      value={form.first_name}
                      onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition-all"
                      placeholder="Onyx-Lord"
                      required
                      data-testid="carte-input-firstname"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                      Nom
                    </label>
                    <input
                      type="text"
                      value={form.last_name}
                      onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition-all"
                      placeholder="FANOU"
                      required
                      data-testid="carte-input-lastname"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition-all"
                    placeholder="votre@email.com"
                    required
                    data-testid="carte-input-email"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                    Téléphone / WhatsApp
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition-all"
                    placeholder="+229 01 90 00 00 00"
                    required
                    data-testid="carte-input-phone"
                  />
                </div>

                {/* Sélecteur formule (rapide) */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">
                    Formule
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {tiers.map((t) => {
                      const isActive = selectedTier === t.slug;
                      return (
                        <button
                          key={t.name}
                          type="button"
                          onClick={() => setSelectedTier(t.slug)}
                          className={`px-3 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${
                            isActive
                              ? 'border-orange-500 bg-orange-50 text-orange-700'
                              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                          }`}
                          data-testid={`carte-tier-select-${t.slug}`}
                        >
                          <div>{t.name}</div>
                          <div className="text-xs text-slate-500 mt-0.5">{t.price}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700" data-testid="carte-error">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-4 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-bold text-lg transition-all shadow-lg hover:shadow-xl disabled:cursor-not-allowed"
                  data-testid="carte-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Traitement…
                    </>
                  ) : selectedTier === 'decouverte' ? (
                    'Adhérer gratuitement'
                  ) : (
                    'Adhérer et payer'
                  )}
                </button>

                <p className="text-xs text-slate-400 text-center leading-relaxed">
                  {selectedTier === 'decouverte'
                    ? 'Adhésion immédiate, aucun paiement.'
                    : 'Vous serez redirigé vers FedaPay (Mobile Money, carte bancaire).'}
                  {' '}En adhérant, vous acceptez notre{' '}
                  <a href="/#faq" className="underline hover:text-orange-500">politique de confidentialité</a>.
                </p>
              </form>
            )}

            <div className="text-center mt-8">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors"
                data-testid="carte-back-home"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour à l'accueil
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CartMembrePage;
